import { BehaviorSubject, map, Observable, withLatestFrom } from 'rxjs';
import { PaginationState } from 'src/app/interfaces/pagination.interface';

import AppValues from 'src/app/common/app.values';
import { SortingOrderType, SortParams } from 'src/app/interfaces/search.interface';
import { MaybeNull } from 'src/app/utils';
import { SortCategories } from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';

export enum ListMode {
  BROWSE = 'BROWSE',
  SEARCH = 'SEARCH',
}

export interface ListState<TContext, TFilter> {
  context: TContext;
  mode: ListMode;
  filtering: TFilter;
  queryKeyword: string;
  sorting: SortCategories;
  pagination: PaginationState;
}

export const INITIAL_PAGINATION: PaginationState = {
  totalItemsCount: 0,
  currentPage: 1,
  limit: AppValues.PAGE_LIMIT,
};

export const paginationParams: (pagination: PaginationState) => [offset: number, limit: number] = (
  pagination: PaginationState,
): [offset: number, limit: number] => {
  const limit = pagination.limit ?? AppValues.PAGE_LIMIT;

  const offset = limit * (pagination.currentPage - 1) || 0;

  return [offset, limit];
};

export const toSortCategories: (sorting: MaybeNull<string>, defaultValue: SortCategories) => SortCategories = (
  sorting: MaybeNull<string>,
  defaultValue: SortCategories,
) => {
  switch (sorting) {
    case 'date_asc':
      return SortCategories.DATE_ASC;
    case 'date_desc':
      return SortCategories.DATE_DESC;
    case 'name_asc':
      return SortCategories.NAME_ASC;
    case 'name_desc':
      return SortCategories.NAME_DESC;
    case 'score_desc':
      return SortCategories.SCORE_DESC;

    default:
      console.error(`Unknown sorting param. Using default sorting param - ${defaultValue}`);
      return defaultValue;
  }
};

export const sortParams: Record<SortCategories, SortParams> = {
  [SortCategories.DATE_ASC]: { ascending: true, orderType: SortingOrderType.DATE },
  [SortCategories.DATE_DESC]: { ascending: false, orderType: SortingOrderType.DATE },
  [SortCategories.NAME_ASC]: { ascending: true, orderType: SortingOrderType.NAME },
  [SortCategories.NAME_DESC]: { ascending: false, orderType: SortingOrderType.NAME },
  [SortCategories.SCORE_DESC]: { ascending: false, orderType: SortingOrderType.SCORE },
};

// TODO: encapsulate common list operations in controller
//   1. update keyword state (switch between BROWSE/SEARCH mode)
//   2. sorting
//   3. update pagination with last item

export class ListController<TContext, TFilter> {
  private readonly notifier$$: BehaviorSubject<void>;
  private readonly state$$: BehaviorSubject<ListState<TContext, TFilter>>;

  private readonly _initialState: ListState<TContext, TFilter>;

  public constructor(state: ListState<TContext, TFilter>) {
    this.state$$ = new BehaviorSubject<ListState<TContext, TFilter>>(state);
    this.notifier$$ = new BehaviorSubject<void>(undefined);

    this._initialState = state;
  }

  public get state$(): Observable<ListState<TContext, TFilter>> {
    return this.state$$;
  }

  public get params$(): Observable<ListState<TContext, TFilter>> {
    return this.notifier$$.pipe(
      withLatestFrom(this.state$),
      map(([_, state]: [void, ListState<TContext, TFilter>]) => state),
    );
  }

  public get initialState(): ListState<TContext, TFilter> {
    return this._initialState;
  }

  public get snapshot(): ListState<TContext, TFilter> {
    return this.state$$.value;
  }

  public get pagination(): PaginationState {
    return this.snapshot.pagination;
  }

  public update(state: ListState<TContext, TFilter>): void {
    this.state$$.next(state);
    this.notifier$$.next();
  }

  public patch(partialState: Partial<ListState<TContext, TFilter>>): void {
    this.state$$.next({ ...this.state$$.value, ...partialState });
    this.notifier$$.next();
  }

  public updateCount(count: number): void {
    const { pagination } = this.state$$.value;

    this.state$$.next({ ...this.state$$.value, pagination: { ...pagination, totalItemsCount: count } });
  }

  public updatePage(page: number): void {
    const { pagination } = this.state$$.value;

    this.state$$.next({ ...this.state$$.value, pagination: { ...pagination, currentPage: page } });
  }

  public triggerParams(): void {
    this.notifier$$.next();
  }
}
